import React, { useState, useEffect, useRef } from "react";
import debounce from "lodash/debounce";
import { toError } from "@wss/error-tracking/utils";
import { logException } from "@wss/error-tracking";
import { Throwable } from "@wss/error-tracking/throwable";
import Popover from "~/common/Popover";
import { CompatibleModelItem } from "./ProductDetailPageResult";
interface Props {
  compatibleModelItems: CompatibleModelItem[];
  compatibleModelItemsCount: number;
  itemNumber: string;
}
const pageSize = 20;
const CompatibleModels = ({
  compatibleModelItems,
  compatibleModelItemsCount,
  itemNumber
}: Props) => {
  const [displayedItems, setDisplayedItems] = useState(compatibleModelItems);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [pageNumber, setPageNumber] = useState(2);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const currentRef = ref.current;
    const handleScroll = () => {
      if (currentRef) {
        const currentPositon = currentRef.scrollTop;
        setScrollPosition(currentPositon);
      }
    };
    if (currentRef) {
      currentRef.addEventListener("scroll", debounce(handleScroll, 150));
    }
    return () => {
      if (currentRef) {
        currentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);
  const updateDisplayedItems = async (): Promise<Throwable> => {
    try {
      const result = await fetch(`/api/productDetails/compatibleModels?item=${itemNumber}&pageSize=${pageSize}&page=${pageNumber}&showHidden=true`);
      const data = await result.json<{
        itemNumberId: number;
        urlLabel: string;
        link: string;
      }[]>();
      setDisplayedItems(prev => [...prev, ...data]);
      return {
        isError: false
      };
    } catch (e) {
      const error = new Error("Error in updateDisplayedItems", {
        cause: toError(e)
      });
      await logException(error);
      setDisplayedItems([]);
      return {
        error,
        isError: true
      };
    }
  };
  useEffect(() => {
    if (ref.current) {
      const shouldGetNextPage = ref.current.scrollHeight - scrollPosition;
      const nextPageBool = shouldGetNextPage < 200 && displayedItems.length < compatibleModelItemsCount;
      if (nextPageBool) {
        setPageNumber(prev => prev + 1);
        updateDisplayedItems();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPosition]);
  return <div className="parts-comp-table box padded" data-sentry-component="CompatibleModels" data-sentry-source-file="CompatibleModels.tsx">
      <p className="header-3 clears">
        {compatibleModelItemsCount}{" "}
        {compatibleModelItemsCount === 1 ? "Compatible Model" : "Compatible Models"}
        &nbsp;
        <Popover title="Compatible Models" options={{
        placement: "top",
        trigger: "hover"
      }} triggerElement={(ref: React.RefObject<HTMLAnchorElement>) => <span className="more-info">
              <a href="#compatible-models" ref={ref} onClick={e => e.preventDefault()}>
                <i className="icon-question" />
              </a>
            </span>} data-sentry-element="Popover" data-sentry-source-file="CompatibleModels.tsx">
          This item is compatible with the listed models, according to the
          manufacturer. We do our best to keep this listing as accurate and
          up-to-date as possible. If you have any questions or concerns, feel
          free to contact a Customer Solutions Specialist before placing your
          order.
        </Popover>
      </p>
      <div className="parts-overflow" ref={ref}>
        <table className="table table-stylized table-striped">
          <tbody data-testid="compatible-models-table-body">
            {displayedItems.map(model => <tr key={model.itemNumberId.toString() + model.urlLabel}>
                <td>
                  {model.link ? <a href={model.link}>{model.urlLabel} </a> : <span>{model.urlLabel} </span>}
                </td>
              </tr>)}
            {displayedItems.length < compatibleModelItemsCount && <tr key="loading">
                <td>Loading more products...</td>
              </tr>}
          </tbody>
        </table>
      </div>
    </div>;
};
export default CompatibleModels;